import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addErrorNotification, addSuccessNotification, getCurrentMemberSubscription, getMember, getPrimaryMemberPaymentCard, loadMemberPaymentCards, NotificationType } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { MemberSubscriptionStatus } from '@vista-digital/ocapi-types/v1';
import { useMountEffect } from '@vista-digital/react-tools';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useReducer } from 'react';
import { useI18n } from '../../context/occ/hooks/use-i18n';
import { useOccConfig } from '../../context/occ/hooks/use-occ-config';
import { getIdForComponentNotification } from '../../utils/get-id-for-component-notification';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { changeSubscriptionToVistaPayment } from './actions/change-subscription-to-vista-payment';
import { makePrimaryCard } from './actions/make-primary-card';
import { reactivateSubscription } from './actions/reactivate-subscription';
import { MemberPaymentCardWalletMessage, MemberPaymentCardWalletMessageType } from './subcomponents/message';
import { MemberPaymentCardWalletReactivateModal } from './subcomponents/modals/reactivate-modal';
import { MemberPaymentCardWalletNotification } from './subcomponents/notification';
import { MemberPaymentCardWalletSkeleton } from './subcomponents/skeleton';
import { MemberPaymentCardWalletView } from './subcomponents/view';
import { mapMemberPaymentCardWalletContext, MemberPaymentCardWalletContextInstance } from './types/context';
import { defaultMemberPaymentCardWalletProps, memberPaymentCardWalletPropsValidator } from './types/props';
import { defaultMemberPaymentCardWalletState } from './types/state';
import { memberPaymentCardWalletActionReducer } from './utils/action-reducer';
import { MemberPaymentCardWalletActionType as ActionType } from './utils/action-type';
const b = bemClassBuilder('v-member-payment-card-wallet');
export const MemberPaymentCardWalletBase = (props, ref) => {
    const i18n = useI18n();
    const { enableSkeletonLoadingIndicators, member: { paymentCards: { expiringSoonThresholdInMonths } } } = useOccConfig();
    const { config, displayText, paymentMethodId } = props;
    const { redirectReturnUrl } = config;
    const [state, dispatch] = useReducer(memberPaymentCardWalletActionReducer, defaultMemberPaymentCardWalletState);
    const { memberPaymentCardWalletNotificationType, isLoading, hasFailedToLoad, showModal, disableActionButtons } = state;
    const subscription = getCurrentMemberSubscription();
    const subscriptionStatus = subscription === null || subscription === void 0 ? void 0 : subscription.status;
    const isUsingThirdPartyPaymentProvider = !!(subscription === null || subscription === void 0 ? void 0 : subscription.billingDetails.isUsingThirdPartyPaymentProvider);
    const primaryCard = getPrimaryMemberPaymentCard();
    const reactivationPrice = subscription === null || subscription === void 0 ? void 0 : subscription.billingDetails.reactivationPrice;
    useMountEffect(() => {
        (async () => {
            dispatch({ type: ActionType.Loading });
            try {
                await loadMemberPaymentCards();
                dispatch({ type: ActionType.Loaded, expiringSoonThresholdInMonths });
            }
            catch (_a) {
                dispatch({ type: ActionType.FailedToLoad });
            }
        })();
    });
    useEffect(() => {
        if (!isLoading && !hasFailedToLoad) {
            dispatch({ type: ActionType.Loaded, expiringSoonThresholdInMonths });
        }
    }, [
        subscriptionStatus,
        isUsingThirdPartyPaymentProvider,
        primaryCard,
        isLoading,
        hasFailedToLoad,
        expiringSoonThresholdInMonths
    ]);
    useImperativeHandle(ref, () => ({
        handleProceed
    }));
    const handleProceed = useCallback(() => {
        if (!getPrimaryMemberPaymentCard()) {
            addErrorNotification({
                id: getIdForComponentNotification('member-payment-card-wallet', NotificationType.Error, 'primary-card-not-set'),
                messages: [displayText.primaryCardNotSetMessage]
            });
            return false;
        }
        return true;
    }, [displayText.primaryCardNotSetMessage]);
    const member = getMember();
    if (!member)
        return null;
    const onCardAdded = async (card, makePrimaryChosen) => {
        dispatch({ type: ActionType.Loading });
        if (makePrimaryChosen) {
            await makePrimaryCard(card, displayText, i18n, true);
            if (subscriptionStatus === MemberSubscriptionStatus.Frozen) {
                await reactivateSubscription(displayText);
            }
        }
        else {
            addSuccessNotification({
                id: getIdForComponentNotification('member-payment-card-wallet', NotificationType.Success, 'add-card'),
                messages: [displayText.addCardSucceededMessage]
            });
        }
        dispatch({ type: ActionType.Loaded, expiringSoonThresholdInMonths });
    };
    const context = mapMemberPaymentCardWalletContext(props, onCardAdded);
    const handleChangePaymentProviderToVistaClicked = async () => {
        if (!primaryCard)
            return;
        dispatch({ type: ActionType.Loading });
        await changeSubscriptionToVistaPayment(primaryCard, displayText, i18n);
        dispatch({ type: ActionType.Loaded, expiringSoonThresholdInMonths });
    };
    if (hasFailedToLoad) {
        return (_jsx(MemberPaymentCardWalletContextInstance.Provider, Object.assign({ value: context }, { children: _jsx(MemberPaymentCardWalletMessage, { messageType: MemberPaymentCardWalletMessageType.FailedToLoadData }) })));
    }
    if (isLoading)
        return enableSkeletonLoadingIndicators ? _jsx(MemberPaymentCardWalletSkeleton, {}) : null;
    const openModal = () => {
        dispatch({ type: ActionType.OpenModal });
    };
    const closeModal = () => {
        dispatch({ type: ActionType.CloseModal });
    };
    const memberPaymentCardWalletViewProps = {
        subscription,
        disableButtons: disableActionButtons,
        onActionStarted: () => dispatch({ type: ActionType.AsyncCallStarted }),
        onActionFinished: () => dispatch({ type: ActionType.AsyncCallCompleted }),
        paymentMethodId,
        redirectReturnUrl,
        primaryCard
    };
    const reactivateModalProps = {
        show: showModal,
        primaryCard,
        reactivationPrice,
        hideModal: closeModal,
        onActionStarted: () => dispatch({ type: ActionType.AsyncCallStarted }),
        onActionFinished: () => dispatch({ type: ActionType.AsyncCallCompleted })
    };
    return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsxs(MemberPaymentCardWalletContextInstance.Provider, Object.assign({ value: context }, { children: [_jsx(MemberPaymentCardWalletReactivateModal, Object.assign({}, reactivateModalProps)), memberPaymentCardWalletNotificationType && (_jsx(MemberPaymentCardWalletNotification, { messageType: memberPaymentCardWalletNotificationType, primaryCard: primaryCard, onFrozenMessageClicked: openModal, onChangePaymentProviderToVistaClicked: handleChangePaymentProviderToVistaClicked })), _jsx(MemberPaymentCardWalletView, Object.assign({}, memberPaymentCardWalletViewProps))] })) })));
};
export const MemberPaymentCardWallet = observer(forwardRef(MemberPaymentCardWalletBase));
MemberPaymentCardWallet.displayName = 'MemberPaymentCardWallet';
/**
 * The **Member Payment Card Wallet** component allows a member to add or remove payment methods linked to their account. They can also set their primary card for payments.
 */
export const MemberPaymentCardWalletComponent = withComponentWrapper(MemberPaymentCardWallet, defaultMemberPaymentCardWalletProps, memberPaymentCardWalletPropsValidator);
